import angular from 'angular';
import template from './templates/wizard.tpl.html';

const USER_REQUESTED_CLOSE = 'cancel';

class WizardController {

  static get $inject() {
    return ['$scope', '$rootScope', '$controller', '$translate', '$q', '$log', '$templateCache',
            'statusMessage', 'messageBox'];
  }

  constructor($scope, $rootScope, $controller, $translate, $q, $log, $templateCache,
              statusMessage, messageBox) {
    const options = $scope.options;

    $scope.wizard = this;

    $scope.$on('modal.closing', (event, reason, isClose) => {
      // only in the case where we want to explicitly close, do we bypass this block
      if (reason !== USER_REQUESTED_CLOSE && !isClose) {
        // this stops the close from taking place and subverts the call to the messagebox result
        event.preventDefault();
        this.messageBox.showQuestion({
          title: 'components.wizard.cancelMessageTitle',
          headline: 'components.wizard.cancelMessage',
          text: ''
        }).result.then(() => {
          this.instance.dismiss(USER_REQUESTED_CLOSE);
        });
      }
    });

    this.$translate = $translate;
    this.$q = $q;
    this.statusMessage = statusMessage;
    this.$log = $log;
    this.messageBox = messageBox;
    this.processing = false;

    this.contentScope = options.contentScope ? options.contentScope.$new() : $rootScope.$new();
    this.contentScope.processing = this.processing;
    this.contentScope.setOnSubmit = fn => this.onSubmit = fn;
    this.contentScope.close = () => this.close();
    this.contentScope.resetSteps = (stepNumber) => this.resetSteps(stepNumber);

    let contentController;

    if (angular.isDefined(options.contentController)) {
      contentController = $controller(
        options.contentController, angular.extend(
          {$scope: this.contentScope}, options.contentResolve || {}
        )
      );
    }

    if (options.contentControllerAs && contentController) {
      this.contentScope[options.contentControllerAs] = contentController;
    }

    this.icon = options.icon;
    this.doNotShowMessage = Boolean(options.doNotShowMessage);

    const keys = {
      TITLE: options.title || 'components.wizard.title',
      CANCEL: options.cancelLabel || 'components.wizard.label.cancel',
      PREVIOUS: options.previousLabel || 'components.wizard.label.previous',
      NEXT: options.nextLabel || 'components.wizard.label.next',
      SUBMIT: options.submitLabel || 'components.wizard.label.submit',
      SUCCESS_MSG: options.successMessage || 'components.wizard.successMessage',
      ERROR_MSG: options.errorMessage || 'components.wizard.errorMessage',
      CANCEL_MSG_TITLE: 'components.wizard.cancelMessageTitle',
      CANCEL_MSG: 'components.wizard.cancelMessage'
    };

    $translate(keys.TITLE, options.titleValues)
      .then(value => this.title = value)
      .catch(value => this.title = value);
    $translate(keys.CANCEL, options.cancelLabelValues)
      .then(value => this.cancelLabel = value)
      .catch(value => this.cancelLabel = value);
    $translate(keys.PREVIOUS, options.previousLabelValues)
      .then(value => this.previousLabel = value)
      .catch(value => this.previousLabel = value);
    $translate(keys.NEXT, options.nextLabelValues)
      .then(value => this.nextLabel = value)
      .catch(value => this.nextLabel = value);
    $translate(keys.SUBMIT, options.submitLabelValues)
      .then(value => this.submitLabel = value)
      .catch(value => this.submitLabel = value);
    $translate(keys.SUCCESS_MSG, options.successMessageValues)
      .then(value => this.successMessage = value)
      .catch(value => this.successMessage = value);
    $translate(keys.ERROR_MSG, options.errorMessageValues)
      .then(value => this.submitErrorMessage = value)
      .catch(value => this.submitErrorMessage = value);

    options.steps = options.steps || [];

    this.instance = options.instance;

    this.instance.result.finally(() => this.contentScope.$destroy());

    options.steps.forEach((step, i) => {
      $translate(step.name, step.nameValues)
        .then(value => step.name = value)
        .catch(value => step.name = value);

      step.id = i;
      step.validated = false; // validated tells us if the step was valid
      if (!step.template && step.templateId) {
        step.template = $templateCache.get(step.templateId);
      }

      if (!(angular.isDefined(step.template) || angular.isDefined(step.templateUrl))) {
        throw new Error('Wizard template or templateUrl option required');
      }

      if (i === 0) {
        step.visited = true;
      }
    });

    this.altStepStyle = typeof options.altStepStyle === 'boolean' ? options.altStepStyle : false;
    this.steps = options.steps;
    this.$scope = $scope;
    this.activateStep(0, true);
  }

  resetSteps(stepNumber) {
    if (!Number.isInteger(stepNumber)) {
      throw new Error('Step Number parameter must be an integer');
    }

    for (let index = stepNumber + 1; index < this.steps.length; index++) {
      this.steps[index].visited = false;
      this.steps[index].validated = false;
    }
  }

  getNextLabel() {
    // if last step, return submitLabel
    if (this.isLastStep) {
      return this.submitLabel;
    } else {
      return this.nextLabel;
    }
  }

  goForward() {
    if (this.isLastStep) {
      this.disposeCurrentStep()
          .then(() => this.submit());
    } else {
      this.nextStep();
    }
  }

  close(returnValue) {
    if (angular.isDefined(this.instance)) {
      this.instance.close(returnValue);
      this.contentScope.$destroy();
      this.$scope.$destroy();
    }
  }

  currentStep() {
    return this.steps[this.stepIndex];
  }

  previousStep() {
    if (!this.isFirstStep) {
      this.activateStep(this.stepIndex - 1);
    }
  }

  nextStep() {
    if (!this.isLastStep) {
      this.activateStep(this.stepIndex + 1);
    }
  }

  get isFirstStep() {
    return this.stepIndex === 0;
  }

  get isLastStep() {
    return this.stepIndex === this.steps.length - 1;
  }

  isValid(stepNumber) {
    const step = Number.isInteger(stepNumber) ? this.steps[stepNumber] : this.currentStep();

    if (!step) {
      return false;
    }

    if (!angular.isFunction(step.validate)) {
      const isFormValid = !!this.contentScope.wizardForm && !!this.contentScope.wizardForm.$valid;
      step.validated = isFormValid;
      return isFormValid;
    }

    const validateFuncResult = step.validate(this.contentScope);
    step.validated = validateFuncResult;
    return validateFuncResult;
  }

  startProcessing() {
    this.processing = true;
    this.contentScope.processing = true;
  }

  stopProcessing() {
    this.processing = false;
    this.contentScope.processing = false;
  }

  showErrorMessage(errorResult) {
    this.stopProcessing();

    let translatePromise;

    if (angular.isString(errorResult)) {
      translatePromise = this.$translate(errorResult)
        .then(value => this.errorMessage = value)
        .catch(value => this.errorMessage = value);
    } else if (angular.isObject(errorResult) && errorResult.i18nKey) {
      translatePromise = this.$translate(errorResult.i18nKey, errorResult.i18nKeyValues)
        .then(value => this.errorMessage = value)
        .catch(value => this.errorMessage = value);
    } else {
      this.errorMessage = this.defaultErrorMessage;
    }

    return this.$q.when(translatePromise).then(() => this.$q.reject(errorResult));
  }

  activateStep(stepNumber, init) {

    const goToStep = () => {
      this.stepIndex = stepNumber;
      this.currentStep().visited = true;
      this.stopProcessing();
      this.errorMessage = null;
    };

    const initializeStep = () => {
      var initializeCallback = angular.isFunction(this.steps[stepNumber].initialize)
          ? this.steps[stepNumber].initialize
          : () => true;

      this.$q.when(initializeCallback(this.contentScope))
            .then(angular.bind(this, goToStep))
            .catch(reason => {
              if (init) {
                // needed to disable previous button
                this.initFailed = true;
              }
              return this.showErrorMessage(reason);
            });
    };

    this.disposeCurrentStep()
      .then(initializeStep);
  }

  cancel(reason) {
    this.instance.dismiss(reason);
  }

  disposeCurrentStep() {
    const validateDisposeCallback = () => {
      let disposeCallback = () => true;
      if (angular.isFunction(this.currentStep().dispose)) {
        disposeCallback = this.currentStep().dispose;
      }
      return disposeCallback(this.contentScope);
    };

    this.startProcessing();

    //validate the form before conducting dispose and store the value
    if (angular.isDefined(this.currentStep())) {
      this.currentStep().validated = this.isValid();
      return this.$q
        .when(validateDisposeCallback())
        .catch((reason) => this.showErrorMessage(reason));
    }

    return this.$q.resolve(undefined);
  }

  jumptToVisitedStep(stepNumber) {
    if (this.steps[stepNumber].visited && this.previousStepsValid(stepNumber)) {
      this.activateStep(stepNumber);
    }
  }

  previousStepsValid(stepNumber) {
    for (let i = 0; i < stepNumber; i++) {
      if (!this.steps[i].validated) {
        return false;
      }
    }
    return true;
  }

  stepClasses(stepNumber) {
    const maxStepIndex = this.steps.length - 1;
    let current = true;

    if (this.processing) {
      return {};
    }

    if (stepNumber > maxStepIndex) {
      return {};
    } else if (stepNumber < maxStepIndex) {
      current = !this.steps[stepNumber + 1].visited || !this.previousStepsValid(stepNumber + 1);
    }

    return {
      active: stepNumber === this.stepIndex,
      visited: this.steps[stepNumber].visited && this.previousStepsValid(stepNumber),
      current
    };
  }

  submit() {
    let result;

    if (angular.isFunction(this.onSubmit)) {
      result = this.onSubmit();
    } else {
      result = angular.noop;
    }

    if (!result) {
      this.stopProcessing();
      this.errorMessage = this.submitErrorMessage;
      return;
    }

    this.$q.when(result).then(
      (returnValue) => {
        this.stopProcessing();
        this.close(returnValue);
        if (!this.doNotShowMessage) {
          this.statusMessage.showSuccess({i18nKey: this.successMessage});
        }
        this.errorMessage = null;
      }
    ).catch((reason) => this.showErrorMessage(reason || this.submitErrorMessage));
  }

}

function wizard($uibModal, $rootScope) {

  return {

    open(options) {
      const scope = $rootScope.$new();

      options.contentController = options.controller;
      options.contentControllerAs = options.controllerAs;
      options.contentScope = options.scope;
      options.contentResolve = options.resolve;
      Reflect.deleteProperty(options, 'resolve');

      scope.options = options;

      options.instance = $uibModal.open(angular.extend(options, {
        scope,
        backdrop: 'static',
        windowClass: 'wizard',
        template,
        controller: WizardController,
        controllarAs: 'wizard',
        bindToController: true
      }));

      return options.instance;
    }
  };
}

wizard.$inject = ['$uibModal', '$rootScope'];

export default wizard;
